import { zodResolver } from '@hookform/resolvers/zod';
import {
  consumerWebhookUrlInputSchema,
  type ConsumerWebhookUrlInput,
  type ConsumerWebhookUrlInputInput,
} from '@easyhelp/shared';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

export interface WebhookUrlFormProps {
  /** Indirizzo di partenza: quello attuale, o l'ultimo usato prima della disattivazione. */
  defaultUrl: string;
  /** «Attiva la notifica», «Riattiva la notifica» o «Salva l'indirizzo». */
  submitLabel: string;
  /** Se vero l'invio richiede un indirizzo diverso da quello di partenza (modifica). */
  requireChange?: boolean;
  /** Motivo del rifiuto dell'API, mostrato sotto il campo. */
  rejection?: string | null;
  busy?: boolean;
  onSubmit: (input: ConsumerWebhookUrlInput) => void;
}

/**
 * L'indirizzo `https` della notifica, con lo schema condiviso per la forma; `https`, porta e reti
 * ammesse le verifica l'API, che risponde con il motivo (`rejection`). Durante l'invio campo e
 * pulsante restano focalizzabili (sola lettura, `aria-disabled`): il fuoco da tastiera non salta.
 */
export function WebhookUrlForm({
  defaultUrl,
  submitLabel,
  requireChange = false,
  rejection = null,
  busy = false,
  onSubmit,
}: WebhookUrlFormProps) {
  const { control, handleSubmit, setError, formState } = useForm<
    ConsumerWebhookUrlInputInput,
    unknown,
    ConsumerWebhookUrlInput
  >({
    resolver: zodResolver(consumerWebhookUrlInputSchema),
    defaultValues: { url: defaultUrl },
  });

  useEffect(() => {
    if (rejection) setError('url', { type: 'server', message: rejection }, { shouldFocus: true });
  }, [rejection, setError]);

  return (
    <Stack
      component="form"
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ sm: 'flex-start' }}
      onSubmit={handleSubmit((input) => {
        if (!busy) onSubmit(input);
      })}
      noValidate
    >
      <Controller
        name="url"
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <TextField
            {...field}
            inputRef={ref}
            label="Indirizzo della notifica"
            type="url"
            placeholder="https://api.prodotto.it/easyhelp/eventi"
            required
            fullWidth
            size="small"
            slotProps={{ input: { readOnly: busy } }}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? 'Solo https sulla porta standard, raggiungibile da internet.'}
          />
        )}
      />
      <Button
        type="submit"
        variant="contained"
        disabled={requireChange && !formState.isDirty}
        aria-disabled={busy}
        sx={{ flexShrink: 0 }}
      >
        {submitLabel}
      </Button>
    </Stack>
  );
}
